import { isRef, ref, Ref, watch } from "@vue/composition-api";
import { ObjectLiteral } from "@/types/misc";
import { BaseCrudService, BaseEntity } from "@/services/base";
import { VueRouter } from "vue-router/types/router";

export class Committable<T extends ObjectLiteral> {
    public original: Ref<T | null> = ref(null);
    public clone: Ref<T | null> = ref(null);
    public loading = ref(false);
    public dirty = ref(false);
    public onCommit: (clone: T, original?: T) => Promise<T>;
    public beforeCommit: (clone: T, original?: T) => never;
    public afterCommit: (clone: T, original?: T) => never;

    constructor({
        onCommit,
        // beforeCommit,
        // afterCommit,
        original,
    }: {
        onCommit: (clone: T, original?: T) => Promise<T>;
        // beforeCommit: (clone: T, original?: T) => Promise<T>;
        // afterCommit: (result: T) => Promise<T>;
        original?: T;
    }) {
        this.onCommit = onCommit;
        if (original) this.set(original);
        if (!original) this.loading.value = true;

        // Watch the clone. If it changes, set dirty to true
        watch(this.clone, () => (this.dirty.value = true), { deep: true });
    }

    set(original: T) {
        this.original.value = original;
        this.loading.value = false;
        this.revert();
    }

    async commit() {
        try {
            this.loading.value = true;
            if (this.onCommit) {
                const result = await this.onCommit(this.clone.value, this.original.value);
                this.set(result);
            } else {
                this.set(this.clone.value);
            }
        } catch (e) {
            throw e;
        } finally {
            this.loading.value = false;
        }
    }

    revert() {
        this.clone.value = Object.assign({}, this.original.value);
        setTimeout(() => (this.dirty.value = false));
    }
}

export const WrapService = <T extends BaseEntity>(
    service: BaseCrudService<T>,
    {
        // deprecated, service cleanup should handle these 3
        ignoredFields = [],
        patchFields,
        createFields,

        extraFields = {},
        routeIdParam = null,
        router = null,
    }: {
        ignoredFields?: string[];
        extraFields?: ObjectLiteral;
        createFields?: string[];
        patchFields?: string[];
        routeIdParam?: string;
        router?: VueRouter;
    },
) => {
    return async function (clone: T, original?: T): Promise<T> {
        const data = Object.assign({}, clone);
        for (let field of ignoredFields) {
            delete data[field];
        }

        if (clone.id) {
            let fields = {};
            if (patchFields) {
                for (const field of patchFields) {
                    fields[field] = data[field];
                }
            } else {
                fields = data;
            }
            return await service.patch(clone.id, { ...fields, ...extraFields });
        } else {
            let fields = {};
            if (createFields) {
                for (const field of createFields) {
                    fields[field] = data[field];
                }
            } else {
                fields = data;
            }
            const newEntity = await service.create({ ...fields, ...extraFields });
            if (routeIdParam && router) {
                await router.replace({ params: { [routeIdParam]: newEntity.id.toString() } });
            }
            return newEntity;
        }
    };
};
